<template>
  <!-- Responsables légaux (2 au plus) : un patient de la clinique, ou une simple fiche -->
  <div>
    <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
      <div>
        <h6 class="fw-bold text-primary mb-0"><i class="bi bi-people me-1"></i>Responsables légaux</h6>
        <small class="text-muted" v-if="mineur">Patient mineur : indiquez au moins un responsable et son téléphone (conseillé, pas obligatoire).</small>
        <small class="text-muted" v-else>Facultatif pour un adulte (tutelle, personne âgée accompagnée…).</small>
      </div>
      <button v-if="lignes.length < max" type="button" class="btn btn-sm btn-outline-primary" @click="ajouter">
        <i class="bi bi-plus-lg me-1"></i>Ajouter un responsable
      </button>
    </div>

    <div v-if="!lignes.length" class="text-muted fst-italic small mb-2">
      Aucun responsable renseigné.
    </div>

    <div v-for="(l, i) in lignes" :key="l.cle" class="border rounded p-3 mb-2 bg-white">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div class="form-check mb-0">
          <input :id="'principal-' + l.cle" class="form-check-input" type="radio" :checked="l.principal" @change="rendrePrincipal(i)">
          <label class="form-check-label small" :for="'principal-' + l.cle">Contact principal</label>
        </div>
        <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="retirer(i)"><i class="bi bi-x-circle me-1"></i>Retirer</button>
      </div>

      <!-- Responsable déjà patient de la clinique : dossiers liés -->
      <div v-if="l.patientId" class="alert alert-light border d-flex justify-content-between align-items-center py-2 mb-2">
        <span><i class="bi bi-link-45deg me-1"></i>Dossier lié : <strong>{{ l.libelleLie }}</strong></span>
        <button type="button" class="btn btn-sm btn-link p-0" @click="delier(i)">Saisir à la main</button>
      </div>
      <div v-else class="position-relative mb-2">
        <input type="search" class="form-control form-control-sm" placeholder="Déjà patient(e) de la clinique ? Rechercher par nom ou téléphone…"
               v-model="l.recherche" @input="rechercher(i)">
        <ul v-if="l.resultats.length" class="list-group position-absolute w-100 shadow-sm resultats">
          <li v-for="p in l.resultats" :key="p.id" class="list-group-item list-group-item-action small" role="button" @click="lier(i, p)">
            <strong>{{ p.nomComplet }}</strong> · {{ p.numeroDossier }}
            <span class="text-muted" v-if="p.telephone"> · {{ p.telephone }}</span>
          </li>
        </ul>
      </div>

      <div class="row g-2">
        <div class="col-md-3">
          <select class="form-select form-select-sm" v-model="l.lien">
            <option v-for="o in liens" :key="o.valeur" :value="o.valeur">{{ o.libelle }}</option>
          </select>
        </div>
        <template v-if="!l.patientId">
          <div class="col-md-3"><input class="form-control form-control-sm" placeholder="Nom" v-model="l.nom"></div>
          <div class="col-md-3"><input class="form-control form-control-sm" placeholder="Prénom" v-model="l.prenom"></div>
        </template>
        <div :class="l.patientId ? 'col-md-9' : 'col-md-3'">
          <input type="tel" class="form-control form-control-sm" :class="{ 'is-invalid': !telephoneValide(l.telephone) }"
                 :placeholder="l.patientId ? 'Autre téléphone (sinon celui du dossier)' : 'Téléphone'" v-model="l.telephone">
          <div class="invalid-feedback">{{ messageTelephone }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { LIENS_RESPONSABLE } from '@/utils/age'
import { telephoneValide, MESSAGE_TELEPHONE } from '@/utils/validation'

let compteur = 0

/** Ligne éditable à partir d'un responsable lu sur le serveur (responsablesListe). */
export function ligneDepuisServeur(r) {
  return {
    cle: ++compteur,
    patientId: r.patientLie?.id || null,
    libelleLie: r.patientLie ? `${r.nomComplet} (${r.patientLie.numeroDossier || 'dossier'})` : '',
    nom: r.patientLie ? '' : (r.nom === 'À préciser' ? '' : r.nom),
    prenom: r.patientLie ? '' : (r.prenom || ''),
    lien: r.lien || 'AUTRE',
    telephone: r.telephoneSaisi || '',
    principal: !!r.principal,
    recherche: '',
    resultats: [],
  }
}

/** Ce qu'on envoie au serveur (les lignes vides sont ignorées par le serveur). */
export function lignesPourServeur(lignes) {
  return lignes.map(l => ({
    patientId: l.patientId || null,
    nom: l.nom, prenom: l.prenom, lien: l.lien, telephone: l.telephone, principal: l.principal,
  }))
}

export function lignesValides(lignes) {
  return lignes.every(l => telephoneValide(l.telephone))
}

export default {
  name: 'ResponsablesPatient',
  props: {
    modelValue: { type: Array, default: () => [] },
    mineur: { type: Boolean, default: false },
    nomPatient: { type: String, default: '' },
    exclureId: { type: [Number, String], default: null },
  },
  emits: ['update:modelValue'],
  data() {
    return { max: 2, liens: LIENS_RESPONSABLE, messageTelephone: MESSAGE_TELEPHONE, minuteries: {} }
  },
  computed: {
    lignes() { return this.modelValue },
  },
  methods: {
    telephoneValide,
    publier(lignes) { this.$emit('update:modelValue', lignes) },
    ajouter() {
      const premiere = !this.lignes.length
      this.publier([...this.lignes, {
        cle: ++compteur, patientId: null, libelleLie: '', nom: premiere ? this.nomPatient : '', prenom: '',
        lien: premiere ? 'MERE' : 'PERE', telephone: '', principal: premiere, recherche: '', resultats: [],
      }])
    },
    retirer(i) {
      const lignes = this.lignes.filter((_, k) => k !== i)
      if (lignes.length && !lignes.some(l => l.principal)) lignes[0].principal = true
      this.publier(lignes)
    },
    rendrePrincipal(i) {
      this.publier(this.lignes.map((l, k) => ({ ...l, principal: k === i })))
    },
    rechercher(i) {
      const l = this.lignes[i]
      clearTimeout(this.minuteries[l.cle])
      if ((l.recherche || '').trim().length < 2) { l.resultats = []; return }
      this.minuteries[l.cle] = setTimeout(async () => {
        try {
          const r = await api.getItems('patients/recherche', { q: l.recherche.trim() })
          l.resultats = (r.data.data || []).filter(p => String(p.id) !== String(this.exclureId)).slice(0, 8)
        } catch (e) {
          l.resultats = []
        }
      }, 300)
    },
    lier(i, p) {
      const lignes = [...this.lignes]
      lignes[i] = {
        ...lignes[i], patientId: p.id, libelleLie: `${p.nomComplet} (${p.numeroDossier || 'dossier'})`,
        nom: '', prenom: '', recherche: '', resultats: [],
        lien: lignes[i].lien || (p.genre === 'Masculin' ? 'PERE' : 'MERE'),
      }
      this.publier(lignes)
    },
    delier(i) {
      const lignes = [...this.lignes]
      lignes[i] = { ...lignes[i], patientId: null, libelleLie: '' }
      this.publier(lignes)
    },
  },
}
</script>

<style scoped>
.resultats { z-index: 20; max-height: 240px; overflow-y: auto; }
</style>
